import { useQuery } from '@tanstack/react-query';
import { FEATURES, FEATURE_KEYS, type WorkspaceEntitlementsDto } from '@aiai/shared';
import { fetchWorkspaceEntitlements } from '../api';

/** A limit meter: `used` against `limit` (null = unlimited). */
function UsageMeter({
  label,
  used,
  limit,
  format = (n) => String(n),
}: {
  label: string;
  used: number;
  limit: number | null;
  format?: (n: number) => string;
}) {
  const unlimited = limit == null;
  const pct = unlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const atLimit = !unlimited && used >= limit;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-ink-muted">{label}</span>
        <span className={atLimit ? 'font-medium text-danger' : 'font-medium text-ink'}>
          {format(used)}
          <span className="text-ink-faint"> / {unlimited ? 'Unlimited' : format(limit)}</span>
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className={`h-full rounded-full ${atLimit ? 'bg-danger' : 'bg-ink'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Read-only "Plan & usage" card for the current workspace: the plan, what it
 * includes (credential rail, switchable features), and live usage against each quota.
 * Any member may see it — it reads the shared entitlements endpoint, which the
 * server resolves from the workspace plan + admin overrides.
 */
export function PlanUsagePanel({ orgId }: { orgId: string }) {
  const { data, isLoading, isError } = useQuery<WorkspaceEntitlementsDto>({
    queryKey: ['orgEntitlements', orgId],
    queryFn: () => fetchWorkspaceEntitlements(orgId),
  });

  if (isLoading) {
    return (
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-ink">Plan &amp; usage</h2>
        <p className="text-sm text-ink-faint">Loading…</p>
      </section>
    );
  }
  if (isError || !data) {
    return (
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-ink">Plan &amp; usage</h2>
        <p className="text-sm text-ink-faint">Could not load plan details.</p>
      </section>
    );
  }

  const resets = new Date(data.usage.periodResetsAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <section className="card space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Plan &amp; usage</h2>
        <span className="rounded-full border border-line px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-ink">
          {data.plan}
        </span>
      </div>

      <UsageMeter
        label="Prototypes"
        used={data.usage.prototypeCount}
        limit={data.maxPrototypes}
      />
      <UsageMeter
        label={`Spend this month (resets ${resets})`}
        used={data.usage.monthToDateSpendUsd}
        limit={data.monthlySpendCapUsd}
        format={(n) => `$${n.toFixed(2)}`}
      />

      <div className="border-t border-line pt-3">
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-muted">Included features</h3>
        <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
          {FEATURE_KEYS.map((k) => (
            <li key={k} className="flex justify-between gap-2">
              <span className="text-ink-muted">{FEATURES[k].label}</span>
              <span className={data.features[k].enabled ? 'text-ink' : 'text-ink-faint'}>
                {data.features[k].enabled ? 'Included' : 'Not included'}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-ink-muted">Credentials</dt>
        <dd className="text-right text-ink">
          {data.credentialMode === 'shared' ? 'Platform-managed' : 'Your own tokens'}
        </dd>
      </dl>

      {data.credentialMode === 'shared' && (
        <p className="text-xs text-ink-faint">
          This workspace runs on the platform’s managed Claude &amp; GitHub
          credentials — there’s nothing to configure.
        </p>
      )}
    </section>
  );
}
